/* eslint-disable react/no-children-prop */
import { memo, useCallback, useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { parse, stringify } from "qs";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { FormattedMessage, useIntl } from "react-intl";
import SmartDPMap from "app/pages/SmartDP/Map/SmartDPMap";
import AppGlobalsContext from "app/AppGlobalsContext";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import {
	BOARD_CODES,
	DESTINATION_LEVELS,
	MULTI_STAGE_JOURNEY_OPTIONS,
	OFFER_TYPE_LABELS,
	OFFER_TYPES,
	PRODUCT_TAG_CODES,
	QUOTATION_CODE_STATUS,
	SDP_PRODUCTS_FILTERS_KEY,
	TOUR_PRODUCT_TYPES,
} from "app/constants";
import "./SmartDPListing.scss";
import { scrollToPosition } from "app/utils/scroller";
import SpecialOfferBanner from "app/pages/.shared/SpecialOffer/SpecialOfferBanner/SpecialOfferBanner";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import SmartDPListingList from "app/pages/SmartDP/Listing/SmartDPListingList/SmartDPListingList";
import StopoverSidePanelContainer from "app/pages/Stopover/StopoverSidePanel/StopoverSidePanelContainer";

import ListingNoResultMessage from "app/pages/.shared/NoResultMessage/ListingNoResultMessage";
import { shouldShowFavoriteBadge } from "app/utils/utils";
import { getAccommodationsWithDiscount, getUnbeatableOffer } from "app/utils/productUtils";
import {
	comparators as defaultComparators,
	SORT_OPTIONS,
	SortProvider,
} from "app/features/product/productSort";
import {
	FILTER_SELECTION_TYPES,
	FilterProvider,
	OPTION_MODE,
	StarRating,
	useFilterParam,
} from "app/features/product/productFilters";
import { TripadvisorRating } from "app/components/ui/rating/TripadvisorRating";
import { useCurrencySide } from "app/hooks";
import { round50 } from "app/utils/smartDPUtils";
import { computeQuantiles } from "app/utils/math/computeQuantiles";
import { matchesDestinationFilter } from "app/utils/destinationFilterUtils";
import { HotelFilledIcon } from "app/icons/travel";
import MiniSearchContainer from "app/features/SearchEngine/MiniSearch/MiniSearchContainer";

const SDP_FILTERS = Object.values(SDP_PRODUCTS_FILTERS_KEY);

const filterBoardsByFilters = (availableBoards = [], desiredBoards = []) => {
	if (!Array.isArray(desiredBoards) || desiredBoards.length === 0) {
		return availableBoards;
	}

	return availableBoards.filter(board => desiredBoards.includes(board.boardCode));
};

const normalizeDestination = value =>
	String(value || "")
		.trim()
		.toLowerCase();

const getProductDestinationValues = product =>
	(product?.geography?.levels || [])
		.map(level => normalizeDestination(level?.label))
		.filter(Boolean);

const getEffectiveLevelsForDestinationType = (levels = [], destinationType) => {
	if (!Array.isArray(levels) || levels.length === 0) {
		return [];
	}

	let effectiveLevels = levels;

	// Pour un destinationType RESORT, si aucun DISTRICT n’existe, on ajoute un niveau "OTHER"
	if (
		destinationType === DESTINATION_LEVELS.RESORT &&
		!levels.some(level => level.level === DESTINATION_LEVELS.DISTRICT)
	) {
		effectiveLevels = [
			...levels,
			{
				level: DESTINATION_LEVELS.OTHER,
				label: DESTINATION_LEVELS.OTHER,
			},
		];
	}

	if (destinationType === DESTINATION_LEVELS.ZONE) {
		return effectiveLevels.filter(level => level.level === DESTINATION_LEVELS.REGION);
	}

	if (
		destinationType === DESTINATION_LEVELS.COUNTRY ||
		destinationType === DESTINATION_LEVELS.NEARTO ||
		destinationType === DESTINATION_LEVELS.PROVINCE ||
		destinationType === DESTINATION_LEVELS.REGION
	) {
		return effectiveLevels.filter(level => level.level === DESTINATION_LEVELS.RESORT);
	}

	if (destinationType === DESTINATION_LEVELS.RESORT) {
		return effectiveLevels.filter(
			level =>
				level.level === DESTINATION_LEVELS.DISTRICT ||
				level.level === DESTINATION_LEVELS.OTHER
		);
	}

	return effectiveLevels;
};

const extractDestinationLevels = (products, destinationType) => {
	if (!Array.isArray(products) || products.length === 0) {
		return [];
	}

	const labelsMap = new Map();

	products.forEach(product => {
		const levels = product.geography?.levels;
		const filteredLevels = getEffectiveLevelsForDestinationType(levels, destinationType);

		filteredLevels.forEach(level => {
			if (!level?.label) {
				return;
			}
			if (!labelsMap.has(level.label)) {
				labelsMap.set(level.label, {
					label: level.label,
					level: level.level,
				});
			}
		});
	});

	return Array.from(labelsMap.values());
};

const buildDestinationOptions = (products, destinationType) => {
	const destinationLevels = extractDestinationLevels(products, destinationType);

	const sortedLevels = [...destinationLevels].sort((a, b) => {
		if (a.label === DESTINATION_LEVELS.OTHER) {
			return 1;
		}
		if (b.label === DESTINATION_LEVELS.OTHER) {
			return -1;
		}

		return a.label.localeCompare(b.label);
	});

	return sortedLevels.map(destination => {
		return {
			value: normalizeDestination(destination.label),
			label: destination.label,
			...(destination.label === DESTINATION_LEVELS.OTHER && {
				labelKey: destination.label,
			}),
		};
	});
};

const computeCheapestBoard = (availableBoards = [], desiredBoards = []) => {
	const candidates = filterBoardsByFilters(availableBoards, desiredBoards);

	if (!Array.isArray(candidates) || candidates.length === 0) {
		return null;
	}

	return candidates.reduce((prev, curr) => {
		const prevPrice = Number(prev.pricePerTraveller);
		const currPrice = Number(curr.pricePerTraveller);

		if (!Number.isFinite(prevPrice)) {
			return curr;
		}
		if (!Number.isFinite(currPrice)) {
			return prev;
		}

		return currPrice < prevPrice ? curr : prev;
	});
};

const computeAccommodationPricePerTraveller = (product, desiredBoards = []) => {
	const cheapestBoard = computeCheapestBoard(product.availableBoards, desiredBoards);

	if (!cheapestBoard) {
		return NaN;
	}

	return Number(cheapestBoard.pricePerTraveller);
};

// Computes cheapest board info for each accommodation (used for display)
const getAccommodationsWithCorrectBoardAsDefault = (accommodations, desiredBoards = []) => {
	return accommodations.map(accommodation => {
		const cheapestBoard = computeCheapestBoard(accommodation.availableBoards, desiredBoards);

		return {
			...accommodation,
			accommodationLabel: cheapestBoard?.accommodationLabel,
			boardCode: cheapestBoard?.boardCode,
			boardLabel: cheapestBoard?.boardLabel,
			total: Number(cheapestBoard?.pricePerTraveller),
		};
	});
};

const rankBoards = code => {
	const i = BOARD_CODES.indexOf(String(code ?? "").toUpperCase());
	return i === -1 ? 999 : i;
};

const withPinning = (comparator, pinnedId) => {
	if (!pinnedId) {
		return comparator;
	}
	return (a, b) => {
		if (a.id === pinnedId && b.id === pinnedId) {
			return 0;
		}
		if (a.id === pinnedId) {
			return -1;
		}
		if (b.id === pinnedId) {
			return 1;
		}
		return comparator(a, b);
	};
};

const SmartDPListing = ({
	accommodationsList = [],
	unavailableAccommodation = {},
	updateBooking,
	selectedStopoverCityName,
	selectedStopoverDuration,
	saveSDPProduct,
	fetchSDPProductsStatus,
	hasSdpSpecialOffer,
	specialOfferInfos = {},
	smartDPCode,
	sortBy,
	clearQuotation,
	isStopOver,
	isSdp,
	quotationItems = [],
	destinationType,
	currentDestinationLabel,
	currencyCode,
	saveSDPSearchQueryString,
	hideDiscountHBX,
}) => {
	const { formatMessage, formatNumber } = useIntl();
	const { filters } = useFilterParam(SDP_FILTERS);
	const { search, pathname } = useLocation();

	const queryParams = parse(search, { ignoreQueryPrefix: true });

	// This is the accommodation to search and spotlight if present
	const { accommodationId: spotlightedAccommodationId, destinationResortCode } = queryParams;

	const isMapView = pathname.includes("/map");

	const [showStopoverAvailableDurationSidePanel, toggleStopoverAvailableDurationSidePanel] =
		useState(false);
	const currencySide = useCurrencySide(currencyCode);
	const navigate = useNavigate();
	const { track } = useAnalytics();

	const { resolution } = useContext(AppGlobalsContext);

	const spotlightAccommodation = accommodationsList.find(
		accommodation => String(accommodation.id) === spotlightedAccommodationId
	);

	const filteredAccommodationsCount = accommodationsList?.length;

	const sdpListingTitle = isStopOver ? (
		<>
			<HotelFilledIcon width={16} heigth={16} />
			<FormattedMessage
				id="sdp.listing.stopover.title"
				values={{
					city: selectedStopoverCityName,
					count: filteredAccommodationsCount,
				}}
			/>
		</>
	) : (
		<FormattedMessage
			id="listing.title"
			values={{
				count: filteredAccommodationsCount,
			}}
		/>
	);

	const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;
	const handleAccommodationClick = useCallback(
		(accommodation, position) => {
			// like for a flashsale product in /listing, we clear quotation.code
			// Based on whtether quotation.code is undefined or not, we fetch availabilities or not
			// fetch availabilities will reset accommodations and boards codes => user selection can not be persisted
			clearQuotation({ keepFlights: isStopOver });

			const {
				name,
				id,
				total,
				accommodationLabel,
				boardCode,
				boardLabel,
				category,
				country,
				location,
				region,
				resort,
				tripadvisor,
				discountPercentage,
				discountValue,
			} = accommodation;

			updateBooking({
				offer: {
					type: OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION,
				},
			});
			saveSDPProduct(accommodation);

			saveSDPSearchQueryString(search);

			const isFavorite = shouldShowFavoriteBadge(accommodation);
			const unbeatableOfferTag = getUnbeatableOffer(accommodation);
			const isTour = TOUR_PRODUCT_TYPES.includes(accommodation?.productExperience);

			const allTags = [
				...(isTour ? ["TOUR_PRODUCT"] : []),
				...(unbeatableOfferTag ? [unbeatableOfferTag] : []),
				...(isFavorite ? [PRODUCT_TAG_CODES.COUP_DE_COEUR] : []),
			];

			track("sdp_product_clicked", {
				name,
				id,
				total,
				accommodationLabel,
				boardCode,
				boardLabel,
				category,
				country,
				location,
				region,
				resort,
				position,
				discountPercentage,
				isSpotlighted: spotlightedAccommodationId === accommodation.id,
				discountValue,
				sortBy,
				tags: allTags,
				connectivitySessionId: smartDPCode,
				tripadvisorRating: tripadvisor?.rating,
			});

			// Only send boardCode to API when board filter is active
			const hasBoardFilter = filters.boards?.length > 0;
			const queryObject = {
				accommodationId: accommodation.id,
				...(hasBoardFilter && { boardCode: accommodation.boardCode }),
				stopoverDuration: selectedStopoverDuration,
				price: accommodation.total,
			};

			const query = stringify(queryObject);

			navigate(
				{
					pathname: `../booking/quote`,
					search: query,
				},
				{
					state: { code: smartDPCode },
				}
			);
		},
		[isMobile, smartDPCode, selectedStopoverDuration, search, filters.boards]
	);

	const handleSelectedflightRecapClick = useCallback(() => {
		toggleStopoverAvailableDurationSidePanel(true);
	}, []);

	const handleCloseSelectedflightRecap = useCallback(() => {
		toggleStopoverAvailableDurationSidePanel(false);
	}, []);

	const handleToggleMap = useCallback(() => {
		if (isMapView) {
			navigate({ pathname: ".", search }, { replace: true });
			track("show_list_clicked");
		} else {
			navigate({ pathname: "./map", search }, { replace: true });
			track("show_map_clicked");
		}
	}, [isMapView, search]);

	useEffect(() => {
		if (typeof window === "undefined") {
			return;
		}

		const id = requestAnimationFrame(() => {
			scrollToPosition(0, 0);
		});

		// eslint-disable-next-line consistent-return
		return () => cancelAnimationFrame(id);
	}, [filters]);

	const shouldDisplaySpecialOffer =
		hasSdpSpecialOffer &&
		specialOfferInfos?.pages?.includes("sdp/listing") &&
		matchesDestinationFilter(
			specialOfferInfos?.destinations?.map(d => d?.label)?.filter(Boolean),
			[currentDestinationLabel]?.filter(Boolean)
		);

	const computedAccommodations = getAccommodationsWithDiscount(
		getAccommodationsWithCorrectBoardAsDefault(accommodationsList, filters.boards)
	);

	const facetConfigs = useMemo(
		() => [
			{
				facetId: SDP_PRODUCTS_FILTERS_KEY.OFFER_TYPE,
				facetTitle: formatMessage({ id: "product.page.filter.offer.type.label" }),
				filters: [
					{
						field: SDP_PRODUCTS_FILTERS_KEY.OFFER_TYPE,
						title: formatMessage({ id: "product.page.filter.offer.type.label" }),
						valueGetter: product => product.merchandisings,
						buildOptions: products => {
							const hasGC = products.some(product =>
								(product.merchandisings || []).some(m =>
									m?.startsWith(PRODUCT_TAG_CODES.GC)
								)
							);

							const hasOnlyCDCProduct = products.some(product => {
								const merchandisings = product.merchandisings || [];
								return (
									merchandisings?.some(m =>
										m?.startsWith(PRODUCT_TAG_CODES.COUP_DE_COEUR)
									) &&
									!merchandisings?.some(m => m?.startsWith(PRODUCT_TAG_CODES.GC))
								);
							});

							const hasDiscount = products
								.flatMap(p => p.merchandisings || [])
								.some(
									m =>
										m?.startsWith(PRODUCT_TAG_CODES.DISCOUNT) &&
										!hideDiscountHBX
								);

							const offerTypes = [
								...(hasGC ? [PRODUCT_TAG_CODES.GC] : []),
								...(hasOnlyCDCProduct ? [PRODUCT_TAG_CODES.COUP_DE_COEUR] : []),
								...(hasDiscount ? [PRODUCT_TAG_CODES.DISCOUNT] : []),
							];

							return offerTypes.map(merch => ({
								value: merch,
								labelKey: OFFER_TYPE_LABELS[merch] || merch,
							}));
						},
						customMatcher: (product, selectedValues) => {
							const productOfferTypes = product.merchandisings;

							return selectedValues?.some(selectedValue => {
								if (selectedValue === PRODUCT_TAG_CODES.COUP_DE_COEUR) {
									const hasCDC = productOfferTypes?.some(offerType =>
										offerType.includes(PRODUCT_TAG_CODES.COUP_DE_COEUR)
									);
									const hasGC = productOfferTypes?.some(offerType =>
										offerType.includes(PRODUCT_TAG_CODES.GC)
									);
									return hasCDC && !hasGC;
								}
								return productOfferTypes?.some(offerType =>
									offerType.startsWith(selectedValue)
								);
							});
						},
						allowSingleOption: true,
						optionMode: OPTION_MODE.MANUAL_ONLY,
					},
				],
			},
			{
				facetId: SDP_PRODUCTS_FILTERS_KEY.MULTI_STAGE_JOURNEY,
				facetTitle: formatMessage({ id: "multi.stage.journey" }),
				filters: [
					{
						field: SDP_PRODUCTS_FILTERS_KEY.MULTI_STAGE_JOURNEY,
						title: formatMessage({ id: "multi.stage.journey" }),
						valueGetter: product => product.productExperience,
						buildOptions: products => {
							return [...new Set(products.map(product => product.productExperience))]
								.filter(option =>
									Object.keys(MULTI_STAGE_JOURNEY_OPTIONS).includes(option)
								)
								.map(option => ({
									value: option,
									label: formatMessage({
										id: MULTI_STAGE_JOURNEY_OPTIONS[option],
									}),
								}));
						},
						optionMode: OPTION_MODE.MANUAL_ONLY,
						allowSingleOption: true,
					},
				],
			},

			{
				facetId: SDP_PRODUCTS_FILTERS_KEY.BOARDS,
				facetTitle: formatMessage({ id: "product.page.filter.boards.label" }),
				filters: [
					{
						field: SDP_PRODUCTS_FILTERS_KEY.BOARDS,
						title: formatMessage({ id: "product.page.filter.boards.label" }),
						valueGetter: product =>
							(product.availableBoards || []).map(b => b.boardCode),
						labelGetter: (product, values) => {
							return (values ?? []).map(value => {
								return product.availableBoards.find(b => b.boardCode === value)
									?.boardLabel;
							});
						},
						customSort: (a, b) => {
							const ra = rankBoards(a.value);
							const rb = rankBoards(b.value);
							if (ra !== rb) {
								return ra - rb;
							}
							return a.label.localeCompare(b.label);
						},
					},
				],
			},

			{
				facetId: SDP_PRODUCTS_FILTERS_KEY.STARS,
				facetTitle: formatMessage({ id: "product.page.filter.stars.label" }),
				filters: [
					{
						field: SDP_PRODUCTS_FILTERS_KEY.STARS,
						title: formatMessage({ id: "product.page.filter.stars.label" }),
						valueGetter: product => product.category,
						// eslint-disable-next-line
						buildOptionPrefix: ({ value }) => <StarRating rating={Number(value)} />,
						formatLabel: ({ label }) => {
							return formatMessage(
								{ id: "product.page.filter.rating.stars" },
								{ count: Number(label), display: label }
							);
						},
						customSort: (a, b) => b?.value?.localeCompare(a?.value),
					},
				],
			},

			{
				facetId: SDP_PRODUCTS_FILTERS_KEY.GUESTS_RATING,
				facetTitle: formatMessage({ id: "product.page.filter.guests.rating.label" }),
				filters: [
					{
						field: SDP_PRODUCTS_FILTERS_KEY.GUESTS_RATING,
						title: formatMessage({ id: "product.page.filter.guests.rating.label" }),
						selectionMode: FILTER_SELECTION_TYPES.SINGLE,
						buildOptions: products => {
							const availableProductsRatings = [
								...new Set(
									products
										.map(p => Number(p?.tripadvisor?.rating))
										.filter(Number.isFinite)
										.map(Math.floor)
								),
							]
								.sort((a, b) => b - a)
								.filter(rating => rating >= 3);

							return availableProductsRatings.map(rating => ({
								value: `gte_${rating}`,
								labelKey: `product.page.guest.rating.TRIP_ADVISOR.value.${rating}`,
								labelPrefix: <TripadvisorRating rating={rating} />,
							}));
						},
						customMatcher: (product, selectedValues) => {
							const rating = Number(product?.tripadvisor?.rating);
							if (!Number.isFinite(rating)) {
								return false;
							}

							const tokenToMinRating = rawToken => {
								const token = String(rawToken).trim().toLowerCase();
								const KNOWN = { gte_5: 5, gte_4: 4, gte_3: 3 };

								if (token in KNOWN) {
									return KNOWN[token];
								}

								const matches = /^gte_(\d+(?:[._,]\d+)?)$/.exec(token);
								if (!matches) {
									return NaN;
								}

								return Number(matches[1].replace(/[,_]/g, "."));
							};

							const thresholds = Array.from(
								new Set(
									(selectedValues || [])
										.map(tokenToMinRating)
										.filter(Number.isFinite)
								)
							);

							if (thresholds.length === 0) {
								return true;
							}

							const minThreshold = Math.min(...thresholds);
							return rating >= minThreshold;
						},
					},
				],
			},

			{
				facetId: SDP_PRODUCTS_FILTERS_KEY.DESTINATIONS,
				facetTitle: formatMessage({ id: "product.page.filter.destinations.label" }),
				filters: [
					{
						field: SDP_PRODUCTS_FILTERS_KEY.DESTINATIONS,
						title: formatMessage({ id: "product.page.filter.destinations.label" }),
						allowSearch: true,
						search: { placeholderKey: "filter.destination.searchbar.placeholder" },
						buildOptions: products =>
							buildDestinationOptions(products, destinationType),
						valueGetter: product => getProductDestinationValues(product),
						optionMode: OPTION_MODE.MANUAL_ONLY,
					},
				],
			},

			{
				facetId: SDP_PRODUCTS_FILTERS_KEY.PROPERTY_NAME,
				facetTitle: formatMessage({ id: "product.page.filter.property.name.label" }),
				filters: [
					{
						field: SDP_PRODUCTS_FILTERS_KEY.PROPERTY_NAME,
						title: formatMessage({ id: "product.page.filter.property.name.label" }),
						allowSearch: true,
						search: { placeholderKey: "filter.property.name.searchbar.placeholder" },
						valueGetter: product => product.id,
						buildOptions: products => {
							return products
								.filter(
									product =>
										!Object.keys(MULTI_STAGE_JOURNEY_OPTIONS).includes(
											product?.productExperience
										)
								)
								.map(product => ({ value: product.id, label: product.name }))
								.sort((a, b) => a.label.localeCompare(b.label));
						},
						optionMode: OPTION_MODE.MANUAL_ONLY,
						showCount: false,
					},
				],
			},

			{
				facetId: SDP_PRODUCTS_FILTERS_KEY.BUDGET,
				facetTitle: formatMessage({ id: "product.page.filter.budget.mobile.label" }),
				filters: [
					{
						field: SDP_PRODUCTS_FILTERS_KEY.BUDGET,
						title: formatMessage({ id: "product.page.filter.budget.mobile.label" }),
						buildOptions: () => {
							const prices = accommodationsList
								.map(product => product?.total)
								.filter(Number.isFinite);

							const { Q1, Q2, Q3 } = computeQuantiles(prices);
							if (![Q1, Q2, Q3].every(Number.isFinite)) {
								return [];
							}

							const q1 = round50(Q1),
								q2 = round50(Q2),
								q3 = round50(Q3);

							return [
								{ value: `lt_${q1}`, formatLabelOptions: { max: q1 } },
								q1 < q2 && {
									value: `between_${q1}_${q2}`,
									formatLabelOptions: { min: q1, max: q2 },
								},
								q2 < q3 && {
									value: `between_${q2}_${q3}`,
									formatLabelOptions: { min: q2, max: q3 },
								},
								{ value: `gt_${q3}`, formatLabelOptions: { min: q3 } },
							].filter(Boolean);
						},
						formatLabel: ({ formatLabelOptions }) => {
							const { min, max } = formatLabelOptions ?? {};
							const formatNumberOptions = {
								minimumFractionDigits: 0,
								maximumFractionDigits: 0,
								useGrouping: true,
							};

							const formatCurrency = n =>
								formatNumber(n, {
									...formatNumberOptions,
									style: "currency",
									currency: currencyCode,
								});

							const formatDecimal = n =>
								formatNumber(n, { ...formatNumberOptions, style: "decimal" });

							const perPerson = formatMessage({
								id: "general.per.person.suffix.short.label",
							});

							const hasMin = typeof min === "number" && !isNaN(min);
							const hasMax = typeof max === "number" && !isNaN(max);

							if (hasMin && hasMax) {
								const left =
									currencySide === "left"
										? formatCurrency(min)
										: formatDecimal(min);
								const right =
									currencySide === "right"
										? formatCurrency(max)
										: formatDecimal(max);
								return `${left} - ${right} ${perPerson}`;
							}

							if (hasMin) {
								return `> ${formatCurrency(min)} ${perPerson}`;
							}
							if (hasMax) {
								return `< ${formatCurrency(max)} ${perPerson}`;
							}
							return "";
						},
						customMatcher: (product, selectedValues, allFilters) => {
							const selectedBoards = allFilters[SDP_PRODUCTS_FILTERS_KEY.BOARDS];
							const price = computeAccommodationPricePerTraveller(
								product,
								selectedBoards
							);

							if (!Number.isFinite(price)) {
								return false;
							}

							const matchToken = t => {
								if (t.startsWith("lt_")) {
									const max = Number(t.slice(3));
									return price < max;
								}

								if (t.startsWith("gt_")) {
									const min = Number(t.slice(3));
									return price > min;
								}

								if (t.startsWith("between_")) {
									const [, a, b] = t.split("_");
									const min = Number(a);
									const max = Number(b);
									return price >= min && price <= max;
								}

								return false;
							};

							return selectedValues.some(matchToken);
						},
					},
				],
			},
		],
		[
			formatMessage,
			destinationType,
			accommodationsList,
			currencyCode,
			currencySide,
			formatNumber,
		]
	);

	const hasAnyDiscountedAccommodations = computedAccommodations.some(
		accommodation => accommodation?.discountPercentage > 0
	);

	const stopoverSortOptions = [
		...SORT_OPTIONS.filter(option => option.id !== "recommendation"),
		...(!hideDiscountHBX && hasAnyDiscountedAccommodations
			? [{ id: "discount_desc", labelKey: "sort.types.discount" }]
			: []),
	];

	const sdpSortOptions = [
		...SORT_OPTIONS,
		...(!hideDiscountHBX && hasAnyDiscountedAccommodations
			? [{ id: "discount_desc", labelKey: "sort.types.discount" }]
			: []),
	];

	const comparators = useMemo(() => {
		const result = {};

		let optionsToWrap = {};

		if (isStopOver) {
			optionsToWrap = stopoverSortOptions;
		} else if (isSdp) {
			optionsToWrap = sdpSortOptions;
		}

		Object.values(optionsToWrap).forEach(option => {
			const { id } = option;
			// eslint-disable-next-line
			const base = defaultComparators[id] || ((_a, _b) => 0);
			result[id] = withPinning(base, spotlightedAccommodationId);
		});

		return result;
	}, [spotlightedAccommodationId, sdpSortOptions, stopoverSortOptions, isStopOver, isSdp]);

	let listing = (
		<>
			<div className="sdp-listing__special-offer">
				{shouldDisplaySpecialOffer && (
					<SpecialOfferBanner
						background={specialOfferInfos.ui?.background}
						titleColor={specialOfferInfos.ui?.titleColor}
						title={specialOfferInfos.title}
						pictoUrl={specialOfferInfos.ui?.pictoUrl}
					/>
				)}
			</div>

			<Routes>
				<Route
					path="/map"
					element={
						<FilterProvider
							facetConfigs={facetConfigs}
							products={computedAccommodations}
						>
							<SortProvider
								{...(isStopOver && {
									options: stopoverSortOptions,
									defaultSort: "price_asc",
								})}
								{...(isSdp && {
									options: sdpSortOptions,
								})}
								comparators={comparators}
							>
								<SmartDPMap
									isStopOver={isStopOver}
									fetchSDPProductsStatus={fetchSDPProductsStatus}
									spotlightAccommodation={spotlightAccommodation}
									unavailableAccommodation={unavailableAccommodation}
									resolution={resolution}
									onAccommodationClick={handleAccommodationClick}
									specialOfferInfos={specialOfferInfos}
									hasSdpSpecialOffer={hasSdpSpecialOffer}
									handleToggleMap={handleToggleMap}
									title={sdpListingTitle}
									destinationLabel={selectedStopoverCityName}
									destinationResortCode={destinationResortCode}
									quotationItems={quotationItems}
								/>
							</SortProvider>
						</FilterProvider>
					}
				/>

				<Route
					path="/"
					element={
						<FilterProvider
							facetConfigs={facetConfigs}
							products={computedAccommodations}
						>
							<SortProvider
								{...(isStopOver && {
									options: stopoverSortOptions,
									defaultSort: "price_asc",
								})}
								{...(isSdp && {
									options: sdpSortOptions,
								})}
								comparators={comparators}
							>
								<SmartDPListingList
									fetchSDPProductsStatus={fetchSDPProductsStatus}
									spotlightAccommodation={spotlightAccommodation}
									unavailableAccommodation={unavailableAccommodation}
									onAccommodationClick={handleAccommodationClick}
									onSelectedflightRecapClick={handleSelectedflightRecapClick}
									isStopOver={isStopOver}
									quotationItems={quotationItems}
									destinationLabel={selectedStopoverCityName}
								/>
							</SortProvider>
						</FilterProvider>
					}
				/>
			</Routes>
		</>
	);

	if (
		fetchSDPProductsStatus === QUOTATION_CODE_STATUS.FULL_FLIGHT ||
		fetchSDPProductsStatus === QUOTATION_CODE_STATUS.FULL_PROPERTY ||
		fetchSDPProductsStatus === QUOTATION_CODE_STATUS.NOT_FOUND ||
		fetchSDPProductsStatus === QUOTATION_CODE_STATUS.BAD_REQUEST
	) {
		listing = (
			<div className="sdp-listing__error">
				{isMobile && <MiniSearchContainer />}
				<ListingNoResultMessage />
				<div className="sdp-listing__error__icon">
					<i className="icon--bagages" />
				</div>
			</div>
		);
	}

	return (
		<div className="sdp-listing">
			{listing}
			<StopoverSidePanelContainer
				open={showStopoverAvailableDurationSidePanel}
				onOpenChange={toggleStopoverAvailableDurationSidePanel}
				handleCloseSelectedflightRecap={handleCloseSelectedflightRecap}
			/>
		</div>
	);
};

const accommodationItem = PropTypes.shape({
	photos: PropTypes.array,
	gps: PropTypes.object,
	name: PropTypes.string,
	category: PropTypes.number,
	categoryType: PropTypes.string,
	location: PropTypes.string,
	tripadvisor: PropTypes.object,
	holidaycheck: PropTypes.object,
});

SmartDPListing.propTypes = {
	specialOfferInfos: PropTypes.object,
	hasSdpSpecialOffer: PropTypes.bool,
	selectedStopoverCityName: PropTypes.string,
	selectedStopoverDuration: PropTypes.string,
	fetchSDPProductsStatus: PropTypes.string,
	smartDPCode: PropTypes.string,
	sortBy: PropTypes.string,
	updateBooking: PropTypes.func,
	saveSDPProduct: PropTypes.func,
	showMoreSDPProducts: PropTypes.func,
	clearQuotation: PropTypes.func,
	isStopOver: PropTypes.bool,
	isSdp: PropTypes.bool,
	accommodationsList: PropTypes.arrayOf(accommodationItem),
	unavailableAccommodation: PropTypes.object,
	quotationItems: PropTypes.array,
	destinationType: PropTypes.string,
	currentDestinationLabel: PropTypes.string,
	currencyCode: PropTypes.string,
	saveSDPSearchQueryString: PropTypes.func,
	hideDiscountHBX: PropTypes.bool,
};

export default memo(SmartDPListing);
